import { useSelector, useDispatch } from 'react-redux';
import SendMessage from './SendMessageComponent';
import { stopSync, syncMessages } from "../appointment/appointmentFunctions";
import { getRoomInitialSync, verifyAndJoinStaffUser } from "../matrix/matrixFuntions";
import { useState, useRef, useEffect } from "react";
import ChatMessage from './components/ChatMessage';
import ChatHeader from './components/ChatHeader';
import { useDraggable } from './hooks/useDraggable';
import { useMatrixEventHandler } from './hooks/useMatrixEventHandler';
import {
    cleanupOldMessages,
    saveChatMessagesToStorage,
    getChatMessagesFromStorage,
    getRoomMembersFromStorage
} from './hooks/useChatStorage';
import { downloadFile } from './services/fileDownloadService';
import { fetchAuthenticatedImage } from './services/authenticatedImageService';
import { isStaffUser } from '../common';
import StaffAppointmentDetails from './StaffAppointmentDetails';
import PatientAppointmentDetails from './PatientAppointmentDetails';

// Track revoked URLs to make cleanup idempotent
const revokedModalUrls = new Set();

export default function ViewChatModal({ selectedAppointment, serviceTypes }) {
      
    const dispatch = useDispatch();
    const chatDetails = useSelector(state => state.chatDetails);
    const loginDetails = useSelector(state => state.loginDetails);
    const practiceDetails = useSelector(state => state.practiceDetails);
    var showChat = chatDetails.showChat;
    var synapseRoomId = chatDetails.synapseRoomId;
    var loggedInUser = loginDetails.loggedInUser;
    var synapseEvents = chatDetails.synapseEvents;
    
    console.log('=== ViewChatModal Debug ===');
    console.log('showChat:', showChat);
    console.log('synapseRoomId:', synapseRoomId);
    console.log('selectedAppointment:', selectedAppointment);
    console.log('selectedAppointment keys:', selectedAppointment ? Object.keys(selectedAppointment) : 'null');
    console.log('selectedAppointment.created_by_user:', selectedAppointment?.created_by_user);
    var matrixClient = chatDetails.matrixClient;
    
    // Get practice details for appointment details modal
    const practice = practiceDetails?.practice;
    const practiceId = practice?._id || '';
    const token = loginDetails?.jwtToken || "";

    // Track new messages when modal is closed
    const [newMessagesCount, setNewMessagesCount] = useState(0);
    const selectedRoom = chatDetails.selectedRoom;
    const [showImage, setShowImage] = useState(false);
    const [authenticatedImageUrl, setAuthenticatedImageUrl] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showApptDetails, setShowApptDetails] = useState(false);
    const modalRef = useRef(null);

    // Draggable functionality
    const { position, handleMouseDown } = useDraggable({ x: 0, y: 0 });

    // Matrix event handling
    const { handleNewMessage } = useMatrixEventHandler(synapseRoomId, showChat, dispatch, loggedInUser);

    // Component mount/unmount logging
    useEffect(() => {
        cleanupOldMessages(); // Clean up old messages on mount
        return () => {
            // Clean up object URL if it exists (idempotent)
            if (authenticatedImageUrl && typeof authenticatedImageUrl === 'string' && authenticatedImageUrl.startsWith('blob:')) {
                try {
                    if (!revokedModalUrls.has(authenticatedImageUrl)) {
                        URL.revokeObjectURL(authenticatedImageUrl);
                        revokedModalUrls.add(authenticatedImageUrl);
                    }
                } catch (error) {
                    console.warn('Error revoking object URL:', error);
                }
            }
            
        };
    }, [authenticatedImageUrl]);

    // Modal state change logging
    useEffect(() => {
    }, [showChat, synapseRoomId]);

    // Keep exactly one timeline listener attached for the current handler.
    useEffect(() => {
        if (!matrixClient) {
            return undefined;
        }

        matrixClient.on('Room.timeline', handleNewMessage);
        return () => {
            matrixClient.removeListener('Room.timeline', handleNewMessage);
        };
    }, [matrixClient, handleNewMessage]);

    useEffect(() => {
        const initializeChat = async () => {
            if (synapseRoomId) {

                if (!matrixClient) {
                    setIsLoading(false);
                    return;
                }

                // Check room membership for receiver issue
                if (matrixClient) {
                    // Room membership check can be added here if needed
                    matrixClient.getRoom(synapseRoomId);
                }

                setIsLoading(true);
                dispatch({ type: 'setSynapseEvents', payload: [] });

                const storedMessages = getChatMessagesFromStorage(synapseRoomId);
                if (storedMessages && storedMessages.length > 0) {
                    // Filter out invalid messages from stored data
                    const validMessages = storedMessages.filter(msg => msg.event_id && msg.room_id && msg.sender);
                    dispatch({ type: 'setSynapseEvents', payload: validMessages });
                } else {
                    try {
                        // Step 1: Try to sync room messages directly
                        await getRoomInitialSync(synapseRoomId, matrixClient, dispatch);
                    } catch (error) {

                        // Step 2: Check if it's an access error and try to join if there's an invite
                        if (error.httpStatus === 403 || error.errcode === 'M_FORBIDDEN' || error.message?.includes('not in room')) {

                            try {
                                // Get appointment ID from selectedAppointment for audit logging
                                const appointmentId = selectedAppointment?._id || selectedAppointment?.appointment_basic_details?._id;

                                // Attempt to verify and join the room
                                const joinResult = await verifyAndJoinStaffUser(
                                    loggedInUser,
                                    matrixClient,
                                    synapseRoomId,
                                    appointmentId,
                                    dispatch
                                );

                                if (joinResult) {

                                    // Step 3: Sync room messages again after successful join
                                    try {
                                        await getRoomInitialSync(synapseRoomId, matrixClient, dispatch);
                                    } catch (syncError) {
                                        // Even if sync fails, user is in the room, so don't show "No access"
                                        dispatch({ type: 'setSynapseEvents', payload: [] });
                                    }
                                } else {

                                    // Step 4: No access and no invite - show error message
                                    dispatch({
                                        type: 'setErrorMsg',
                                        payload: 'No access'
                                    });
                                    // Close chat modal
                                    dispatch({
                                        type: 'hideChat',
                                    });
                                    // Clear error message after 3 seconds
                                    setTimeout(() => {
                                        dispatch({ type: 'setErrorMsg', payload: null });
                                    }, 3000);
                                    return;
                                }
                            } catch (joinError) {

                                // Step 4: Join failed - show error message
                                dispatch({
                                    type: 'setErrorMsg',
                                    payload: 'No access'
                                });
                                // Close chat modal
                                dispatch({
                                    type: 'hideChat',
                                });
                                // Clear error message after 3 seconds
                                setTimeout(() => {
                                    dispatch({ type: 'setErrorMsg', payload: null });
                                }, 3000);
                                return;
                            }
                        } else {
                            // For other non-access errors, re-throw them
                            throw error;
                        }
                    }
                }

                syncMessages(synapseRoomId, matrixClient, dispatch, loggedInUser, selectedAppointment);

                setTimeout(() => {
                    setIsLoading(false);
                }, 500);
            }
        };

        initializeChat();

        return () => {
            stopSync();
        };
    }, [synapseRoomId, matrixClient, loggedInUser, selectedAppointment, dispatch]);

    useEffect(() => {
        if (showChat && newMessagesCount > 0) {
            // Clear notifications for this specific room when modal opens
            dispatch({ type: 'clearRoomNotifications', payload: synapseRoomId });
            setNewMessagesCount(0);
        }
    }, [showChat, newMessagesCount, synapseRoomId, dispatch]);

    // One-time cleanup of invalid messages in Redux store
    useEffect(() => {
        if (synapseEvents && synapseEvents.length > 0) {
            const invalidMessages = synapseEvents.filter(msg => !msg.event_id || !msg.room_id || !msg.sender);
            if (invalidMessages.length > 0) {
                const validMessages = synapseEvents.filter(msg => msg.event_id && msg.room_id && msg.sender);
                dispatch({ type: 'setSynapseEvents', payload: validMessages });
            }
        }
    }, [dispatch, synapseEvents]); // Run when dispatch or synapseEvents change

    useEffect(() => {
        if (synapseRoomId && synapseEvents && synapseEvents.length > 0) {
            saveChatMessagesToStorage(synapseRoomId, synapseEvents);
        }
    }, [synapseEvents, synapseRoomId, dispatch]);

    // Log message filtering and state changes
    useEffect(() => {
        if (synapseEvents && synapseRoomId) {
            // Filter current room events for debugging purposes
            synapseEvents.filter(event =>
                event.room_id === synapseRoomId
            );
        }
    }, [synapseEvents, synapseRoomId, showChat]);

    // Filter synapseEvents to ensure only messages for current room are displayed
    const currentRoomEvents = synapseEvents && synapseEvents.filter(event => {
        // Filter out invalid messages immediately
        if (!event.event_id || !event.room_id || !event.sender) {
            return false;
        }

        const matches = event.room_id === synapseRoomId;
        if (!matches) {
        }
        return matches;
    });

    // Group messages by date for better organization
    const groupMessagesByDate = (events) => {
        const groups = {};
        if (!events) return groups;
        
        events.forEach(event => {
            const date = new Date(event.origin_server_ts).toDateString();
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(event);
        });
        return groups;
    };

    const messageGroups = groupMessagesByDate(currentRoomEvents);

    // Render date separator
    const renderDateSeparator = (date) => {
        const today = new Date().toDateString();
        const yesterday = new Date(Date.now() - 86400000).toDateString();
        
        let displayDate = date;
        if (date === today) {
            displayDate = 'Today';
        } else if (date === yesterday) {
            displayDate = 'Yesterday';
        } else {
            displayDate = new Date(date).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                year: new Date(date).getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
            });
        }

        return (
            <div className="flex items-center justify-center my-4">
                <div className="bg-gray-100 text-gray-600 text-xs font-medium px-3 py-1 rounded-full">
                    {displayDate}
                </div>
            </div>
        );
    };

    const formatDate = (dateString) => {
        const options = { hour: 'numeric', hour12: true, minute: 'numeric' }
        return new Date(dateString).toLocaleTimeString(undefined, options)
    }

    const handleViewChatClose = () => {
        dispatch({
            type: 'hideChat',
        });
        stopSync();
    }


    const handleViewAppointmentDetails = () => {
        setShowApptDetails(true);
    }

    const handleApptDetailsClose = () => {
        setShowApptDetails(false);
    }

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (currentRoomEvents && currentRoomEvents.length > 0) {
            const chatInnerDiv = document.getElementById('viewChatInnerDiv');
            if (chatInnerDiv) {
                setTimeout(() => {
                    chatInnerDiv.scrollTop = chatInnerDiv.scrollHeight;
                }, 100);
            }
        }
    }, [currentRoomEvents]);

    // Auto-scroll when modal opens
    useEffect(() => {
        if (showChat && currentRoomEvents && currentRoomEvents.length > 0) {
            const chatInnerDiv = document.getElementById('viewChatInnerDiv');
            if (chatInnerDiv) {
                setTimeout(() => {
                    chatInnerDiv.scrollTop = chatInnerDiv.scrollHeight;
                }, 200);
            }
        }
    }, [showChat, currentRoomEvents]);

    const getUserDisplayName = (userId) => {
        const getAppointmentPatientName = () => {
            const patient = selectedAppointment?.created_by_user;
            const patientMatrixId = patient?.synapse_user_id;
            const isCurrentPatient = !isStaffUser(loggedInUser) &&
                userId === loggedInUser?.synapse_user_id;

            if (userId !== patientMatrixId && !isCurrentPatient) {
                return null;
            }

            return `${patient?.firstname || loggedInUser?.firstname || ''} ${
                patient?.lastname || loggedInUser?.lastname || ''
            }`.trim() || null;
        };

        const getReadableMatrixUserId = (matrixUserId) => {
            const localpart = matrixUserId?.match(/^@([^:]+):/)?.[1];
            if (!localpart) {
                return null;
            }

            const username = localpart.replace(
                /-[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
                ''
            );
            if (!username) {
                return null;
            }

            return username
                .split(/[-_.]+/)
                .filter(Boolean)
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                .join(' ');
        };

        const getUsableDisplayName = (candidate) =>
            typeof candidate === 'string' && candidate !== userId && !candidate.startsWith('@')
                ? candidate
                : null;

        console.log('=== getUserDisplayName DEBUG ===');
        console.log('Looking up display name for userId:', userId);
        
        // Try to get from selectedRoom first (if available)
        if (selectedRoom && selectedRoom.currentState && selectedRoom.currentState.members) {
            var members = selectedRoom.currentState.members;
            console.log('Room members available:', Object.keys(members));
            
            if (members[userId]) {
                const memberName = members[userId].name;
                const memberDisplayName = members[userId].displayName;
                console.log('Found member in room:', {
                    userId,
                    name: memberName,
                    displayName: memberDisplayName,
                    rawMember: members[userId]
                });
                
                const displayName = getUsableDisplayName(memberName) ||
                    getUsableDisplayName(memberDisplayName);
                if (displayName) {
                    if (displayName.length > 50) {
                        const patientName = getAppointmentPatientName();
                        if (patientName) {
                            return patientName;
                        }
                    }
                    return displayName;
                }
            }
        }
        
        // Fallback: try to get from cached room members in localStorage
        if (synapseRoomId) {
            const cachedMembers = getRoomMembersFromStorage(synapseRoomId);
            console.log('Cached members for room:', synapseRoomId, cachedMembers ? Object.keys(cachedMembers) : 'none');
            
            if (cachedMembers && cachedMembers[userId]) {
                const cachedName = cachedMembers[userId].name;
                const cachedDisplayName = cachedMembers[userId].displayName;
                console.log('Found member in cache:', {
                    userId,
                    name: cachedName,
                    displayName: cachedDisplayName,
                    rawMember: cachedMembers[userId]
                });
                
                const displayName = getUsableDisplayName(cachedName) ||
                    getUsableDisplayName(cachedDisplayName);
                if (displayName) {
                    if (displayName.length > 50) {
                        const patientName = getAppointmentPatientName();
                        if (patientName) {
                            return patientName;
                        }
                    }
                    return displayName;
                }
            }
        }
        
        // Fallback: try to get from synapseEvents if room members not available
        if (synapseEvents && synapseEvents.length > 0) {
            const senderEvent = synapseEvents.find(event => event.sender === userId);
            if (senderEvent && senderEvent.content) {
                const senderDisplayName = senderEvent.content.sender_displayname;
                console.log('Found display name in event:', {
                    userId,
                    sender_displayname: senderDisplayName,
                    eventContent: senderEvent.content
                });
                const displayName = getUsableDisplayName(senderDisplayName);
                if (displayName) {
                    return displayName;
                }
            }
        }
        
        // Final fallback: try to get patient name from appointment data
        const patientName = getAppointmentPatientName();
        if (patientName) {
            console.log('Using patient name from appointment as final fallback:', patientName);
            return patientName;
        }
        
        // Final fallback: show loading state for staff users, userId for patients
        if (loggedInUser && loggedInUser.user_type === 'STAFF') {
            console.log('Staff user, returning Loading...');
            return 'Loading...';
        }
        
        const readableMatrixUserId = getReadableMatrixUserId(userId);
        console.log('No display name found, using readable Matrix ID:', readableMatrixUserId || userId);
        return readableMatrixUserId || userId;
    };

    const handleShowImageClose = () => {
        // Clean up object URL if it exists (idempotent)
        if (authenticatedImageUrl && typeof authenticatedImageUrl === 'string' && authenticatedImageUrl.startsWith('blob:')) {
            try {
                if (!revokedModalUrls.has(authenticatedImageUrl)) {
                    URL.revokeObjectURL(authenticatedImageUrl);
                    revokedModalUrls.add(authenticatedImageUrl);
                }
            } catch (error) {
                console.warn('Error revoking object URL:', error);
            }
        }

        setShowImage(false);
        setAuthenticatedImageUrl(null);
        dispatch({
            type: 'setSelectedImageUrl',
            payload: null
        });
    }


    const handleImageMessage = (imgUrl, filename) => {
        downloadFile(imgUrl, filename, matrixClient, dispatch);
    }

    const handleImageClick = async (httpUrl, filename, mxcUrl) => {
        setImageLoading(true);
        setAuthenticatedImageUrl(null);

        try {
            if (!mxcUrl || !matrixClient) {
                throw new Error('MXC URL or Matrix client not available');
            }

            // 1. Use the authenticated image service with download URL (not thumbnail)
            const result = await fetchAuthenticatedImage(mxcUrl, matrixClient, false);
            
            setAuthenticatedImageUrl(result.url);
            setShowImage(true);
            
        } catch (err) {
            
            // Show error message to user
            setAuthenticatedImageUrl(null);
            setShowImage(true); // Show modal with error state
        } finally {
            setImageLoading(false);
        }
    };

    const handleDownloadImage = async () => {
        if (!authenticatedImageUrl) return;
        
        try {
            // Get filename from the current image data or use default
            const filename = `image_${Date.now()}.png`;
            
            // Download to downloads folder
            const link = document.createElement('a');
            link.href = authenticatedImageUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
        } catch (err) {
        }
    };


    return (
        <div>
            {showChat && (
                <div className="fixed inset-0 z-50 pointer-events-none">
                    <div className="fixed inset-0 bg-black opacity-50 pointer-events-auto" onClick={handleViewChatClose}></div>

                    <div
                        ref={modalRef}
                        className="absolute bg-white rounded-lg shadow-xl pointer-events-auto w-full max-w-sm lg:w-[400px] lg:max-w-[400px] h-[650px] max-h-[90vh] lg:max-h-[650px] overflow-hidden flex flex-col"
                        style={{
                            transform: `translate(${position.x}px, ${position.y}px)`,
                            right: '1rem',
                            bottom: '1rem'
                        }}
                    >
                        {/* Built-in close button */}
                        <button
                            onClick={handleViewChatClose}
                            className="absolute top-2 right-2 z-10 p-1 text-white bg-teal-700 hover:bg-teal-800 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                            title="Close Chat"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        
                        <ChatHeader
                            selectedAppointment={selectedAppointment}
                            serviceTypes={serviceTypes}
                            loggedInUser={loggedInUser}
                            handleMouseDown={handleMouseDown}
                            onViewAppointmentClick={handleViewAppointmentDetails}
                            matrixClient={matrixClient}
                            practiceId={practiceId}
                            token={token}
                        />

                        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                            <div id="viewChatOuterDiv" className="flex-1 flex flex-col min-h-0">
                                <div id="viewChatInnerDiv" className="bg-[#f3ffe4] flex-1 overflow-y-auto px-2 pb-2">
                                    {isLoading ? (
                                        <div className="flex items-center justify-center h-full">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                        </div>
                                    ) : (
                                        Object.entries(messageGroups).map(([date, events]) => (
                                            <div key={date}>
                                                {renderDateSeparator(date)}
                                                <div className="space-y-3">
                                                    {events.map((sEvent, index) => (
                                                        <ChatMessage
                                                            key={sEvent.event_id || index}
                                                            sEvent={sEvent}
                                                            loggedInUser={loggedInUser}
                                                            getUserDisplayName={getUserDisplayName}
                                                            formatDate={formatDate}
                                                            handleImageMessage={handleImageMessage}
                                                            matrixClient={matrixClient}
                                                            onImageClick={handleImageClick}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className="flex-shrink-0 border-t border-gray-200">
                                    <SendMessage
                                        loggedInUser={loggedInUser}
                                        synapseRoomId={synapseRoomId}
                                        dispatch={dispatch}
                                        handleViewChatClose={handleViewChatClose}
                                    />
                                    
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showImage && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black opacity-50" onClick={handleShowImageClose}></div>

                        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full">
                            <div className="flex items-center justify-between p-4 border-b border-gray-200">
                                <h2 className="text-xl font-semibold text-gray-800">Image Preview</h2>
                                <button
                                    onClick={handleShowImageClose}
                                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="p-4">
                                <div className="flex justify-center">
                                    {imageLoading ? (
                                        <div className="flex flex-col items-center justify-center h-64">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                            <p className="ml-3 text-gray-600 mt-2">Loading image...</p>
                                            <p className="text-xs text-gray-500 mt-1">Using authenticated Matrix API</p>
                                        </div>
                                    ) : authenticatedImageUrl ? (
                                        <div className="text-center">
                                            <img
                                                src={authenticatedImageUrl}
                                                alt="Preview"
                                                style={{ maxWidth: "100%", maxHeight: "500px", height: "auto", borderRadius: "8px" }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    const errorMsg = e.target.nextElementSibling;
                                                    if (errorMsg) {
                                                        errorMsg.style.display = 'block';
                                                    }
                                                }}
                                            />
                                            <div style={{ display: 'none', color: '#dc2626', padding: '20px' }}>
                                                <p>❌ Failed to load image</p>
                                                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                                                    The image could not be loaded. This might be due to a server issue.
                                                </p>
                                            </div>
                                            
                                            
                                            <div className="mt-4">
                                                <button
                                                    onClick={handleDownloadImage}
                                                    className="px-6 py-2 bg-teal-700 text-white font-medium rounded-md hover:bg-teal-800 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 flex items-center gap-2 mx-auto"
                                                    title="Download image"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                    </svg>
                                                    Download Image
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center" style={{ color: '#dc2626', padding: '40px' }}>
                                            <div className="mb-4">
                                                <svg className="w-16 h-16 mx-auto text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                                                </svg>
                                            </div>
                                            <p className="text-lg font-semibold mb-2">❌ Image Not Available</p>
                                            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                                                Unable to load the image using authenticated Matrix API.
                                            </p>
                                            <div style={{ fontSize: '12px', color: '#9ca3af', backgroundColor: '#f9fafb', padding: '12px', borderRadius: '6px', textAlign: 'left', display: 'inline-block' }}>
                                                <p><strong>Tried:</strong></p>
                                                <p>1. � Authenticated Matrix API (mxcUrlToHttp)</p>
                                                <p>2. � Bearer token authentication</p>
                                                <p className="mt-2"><strong>Possible causes:</strong></p>
                                                <p>• Invalid or expired access token</p>
                                                <p>• Media server configuration issue</p>
                                                <p>• Network connectivity problem</p>
                                                <p>• Insufficient permissions</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Appointment Details Modal */}
            {showApptDetails && selectedAppointment && (
                <>
                    {isStaffUser(loggedInUser) ? (
                        <StaffAppointmentDetails
                            selectedAppointment={selectedAppointment}
                            showApptDetails={showApptDetails}
                            handleApptDetailsClose={handleApptDetailsClose}
                            loggedInUser={loggedInUser}
                            dispatch={dispatch}
                            serviceTypes={serviceTypes}
                            practiceId={practiceId}
                            token={token}
                        />
                    ) : (
                        <PatientAppointmentDetails
                            showApptDetails={showApptDetails}
                            handleApptDetailsClose={handleApptDetailsClose}
                            selectedAppointment={selectedAppointment}
                            loggedInUser={loggedInUser}
                            dispatch={dispatch}
                            serviceTypes={serviceTypes}
                        />
                    )}
                </>
            )}
        </div> 
    );
}
